import type {
    EditorAdapter,
    OpenDocumentRef,
    ProposedEdit,
    EditResolution,
    ProposedNodeEdit
} from "@codenexus/react";

export interface AdapterRoute {
    adapter: EditorAdapter;
    /** Returns true if this adapter owns the given docId. */
    owns: (docId: string) => boolean;
}

/**
 * Routes EditorAdapter calls across multiple child adapters by docId
 * ownership, so the host bridge can serve both the prose (TipTap) and code
 * (Monaco) editor surfaces from a single bundle. listOpen() merges all
 * children; read/applyEdit/applyNodeEdit dispatch to the owning child;
 * onChange/onEditResolved fan out and forward.
 */
export class CompositeHostAdapter implements EditorAdapter {
    constructor(private routes: AdapterRoute[]) {}

    private route(docId: string): EditorAdapter | null {
        return this.routes.find((r) => r.owns(docId))?.adapter ?? null;
    }

    listOpen(): OpenDocumentRef[] {
        return this.routes.flatMap((r) => r.adapter.listOpen());
    }

    async read(docId: string): Promise<{ content: string; ref: OpenDocumentRef }> {
        const adapter = this.route(docId);
        if (!adapter) throw new Error("doc_not_open");
        return adapter.read(docId);
    }

    async applyEdit(
        edit: ProposedEdit
    ): Promise<{ status: "staged" } | { status: "failed"; reason: string }> {
        const adapter = this.route(edit.docId);
        if (!adapter) return { status: "failed", reason: "doc_not_open" };
        return adapter.applyEdit(edit);
    }

    async applyNodeEdit(
        edit: ProposedNodeEdit
    ): Promise<{ status: "staged" } | { status: "failed"; reason: string }> {
        const adapter = this.route(edit.docId);
        if (!adapter || !adapter.applyNodeEdit) {
            return { status: "failed", reason: "node_edits_not_supported" };
        }
        return adapter.applyNodeEdit(edit);
    }

    onChange(l: (refs: OpenDocumentRef[]) => void): () => void {
        const unsubs = this.routes.map((r) => r.adapter.onChange(l));
        return () => unsubs.forEach((u) => u());
    }

    onEditResolved(l: (r: EditResolution) => void): () => void {
        const unsubs = this.routes.map((r) => r.adapter.onEditResolved(l));
        return () => unsubs.forEach((u) => u());
    }
}
